import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  CAMPAIGN_STATUS_VALUES,
  CAMPAIGN_VERTICAL_VALUES,
  type CampaignStatus,
  type CampaignVertical,
} from '@content-engine/shared';
import { ApiError, request } from '../../lib/api.ts';
import type { Campaign } from '../../lib/types.ts';

interface CampaignsResponse {
  campaigns: Campaign[];
}

interface CreateCampaignInput {
  name: string;
  vertical: CampaignVertical;
  keywords: string[];
  status: CampaignStatus;
  maxPostsPerDay: number;
  cronSchedule: string;
}

export function CampaignsListPage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => request<CampaignsResponse>('/campaigns'),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      request<{ campaign: Campaign }>('/campaigns', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setShowCreate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => request(`/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const campaigns = campaignsQuery.data?.campaigns ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('campaigns.title')}</h1>
          <div className="sub">{t('campaigns.subtitle')}</div>
        </div>
        <div className="filters">
          <button className="primary" onClick={() => setShowCreate(true)}>
            {t('campaigns.create')}
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h2>
            {t('campaigns.all')} <span className="count">{campaigns.length}</span>
          </h2>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          {campaignsQuery.isLoading && <div style={{ padding: 16 }}>{t('common.loading')}</div>}
          {campaignsQuery.isError && (
            <div style={{ padding: 16, color: 'var(--hot)' }}>
              {t('campaigns.loadError', { message: (campaignsQuery.error as Error).message })}
            </div>
          )}
          {campaigns.length === 0 && !campaignsQuery.isLoading && (
            <div style={{ padding: 24, color: 'var(--text-3)' }}>
              {t('campaigns.noCampaigns')}
            </div>
          )}
          {campaigns.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>{t('campaigns.tableName')}</th>
                  <th>{t('campaigns.tableVertical')}</th>
                  <th>{t('campaigns.tableKeywords')}</th>
                  <th className="num">{t('campaigns.tablePostsPerDay')}</th>
                  <th>{t('campaigns.tableCron')}</th>
                  <th>{t('campaigns.tableStatus')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link
                        to={`/campaigns/${c.id}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        <div className="name" style={{ color: 'var(--neon)' }}>
                          {c.name}
                        </div>
                        <div className="desc">id: {c.id.slice(0, 8)}…</div>
                      </Link>
                    </td>
                    <td>{t(`campaigns.verticals.${c.vertical}`)}</td>
                    <td>{c.keywords.join(', ') || '—'}</td>
                    <td className="num">{c.maxPostsPerDay}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.cronSchedule}</td>
                    <td>
                      <span className={`pill ${c.status === 'active' ? 'live' : ''}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/campaigns/${c.id}`}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--line-strong)',
                          color: 'var(--text-2)',
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 11,
                          textDecoration: 'none',
                          marginRight: 6,
                        }}
                      >
                        {t('campaigns.openButton')}
                      </Link>
                      <button
                        onClick={() => {
                          if (confirm(t('campaigns.deleteConfirm', { name: c.name }))) {
                            deleteMutation.mutate(c.id);
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid rgba(255,61,110,0.3)',
                          color: 'var(--hot)',
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 11,
                          cursor: 'pointer',
                        }}
                      >
                        {t('campaigns.deleteButton')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateCampaignDialog
          onClose={() => setShowCreate(false)}
          onSubmit={(input) => createMutation.mutate(input)}
          submitting={createMutation.isPending}
          error={createMutation.error instanceof ApiError ? createMutation.error.message : null}
        />
      )}
    </>
  );
}

interface CreateCampaignDialogProps {
  onClose: () => void;
  onSubmit: (input: CreateCampaignInput) => void;
  submitting: boolean;
  error: string | null;
}

function CreateCampaignDialog(props: CreateCampaignDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<CampaignVertical>('casino');
  const [keywords, setKeywords] = useState('');
  const [status, setStatus] = useState<CampaignStatus>('paused');
  const [maxPostsPerDay, setMaxPostsPerDay] = useState(5);
  const [cronSchedule, setCronSchedule] = useState('0 */4 * * *');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    props.onSubmit({
      name,
      vertical,
      keywords: keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      status,
      maxPostsPerDay,
      cronSchedule,
    });
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 520,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 4 }}>{t('campaigns.create_dialog.title')}</h2>
        <div style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 20 }}>
          {t('campaigns.create_dialog.subtitle')}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <label>{t('campaigns.create_dialog.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('campaigns.create_dialog.namePlaceholder')}
              required
              autoFocus
            />
          </div>
          <div className="form-row inline">
            <div>
              <label>{t('campaigns.create_dialog.vertical')}</label>
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value as CampaignVertical)}
              >
                {CAMPAIGN_VERTICAL_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {t(`campaigns.verticals.${v}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>{t('campaigns.create_dialog.status')}</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as CampaignStatus)}
              >
                {CAMPAIGN_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <label>{t('campaigns.create_dialog.keywords')}</label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder={t('campaigns.create_dialog.keywordsPlaceholder')}
            />
          </div>
          <div className="form-row inline">
            <div>
              <label>{t('campaigns.create_dialog.postsPerDay')}</label>
              <input
                type="number"
                min={1}
                max={50}
                value={maxPostsPerDay}
                onChange={(e) => setMaxPostsPerDay(Number(e.target.value))}
              />
            </div>
            <div>
              <label>{t('campaigns.create_dialog.cron')}</label>
              <input
                type="text"
                value={cronSchedule}
                onChange={(e) => setCronSchedule(e.target.value)}
              />
            </div>
          </div>

          {props.error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {props.error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={props.onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={props.submitting}>
              {props.submitting
                ? t('campaigns.create_dialog.submitting')
                : t('campaigns.create_dialog.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
